import * as THREE from "three";
import { Signal } from "@engine";
import type { BossMarker, LevelEntry, Shape, ShapeSegment } from "./types";
import { isGate } from "./types";
import type { Segment } from "./SegmentFactory";
import type { SegmentFactory } from "./SegmentFactory";

export interface GateCrossedEvent {
  toShape: Shape;
  zCenter: number;
}

export interface BossEvent {
  marker: BossMarker;
}

const RECYCLE_PAST_PLAYER = 8;
const SPAWN_AHEAD = 60;

export class LevelStreamer {
  readonly onGateCrossed = new Signal<GateCrossedEvent>();
  readonly onShapeChanged = new Signal<Shape>();
  readonly onSpeedChanged = new Signal<number>();
  readonly onLevelComplete = new Signal<void>();
  readonly onSegmentCleared = new Signal<Segment>();
  // Fired the moment a segment carrying `boss: { id, banner }` is
  // attached to the world. The game layer reacts by showing the HUD
  // banner, cueing music, etc.
  readonly onBossStart = new Signal<BossEvent>();
  // Fired when the segment carrying `bossEnd: true` for the active
  // boss recycles past the player. The game layer hides the banner,
  // pays the clear bonus, and resets boss state.
  readonly onBossCleared = new Signal<BossEvent>();

  private factory: SegmentFactory;
  private worldGroup: THREE.Object3D;
  private level: LevelEntry[] = [];
  private cursor = 0;
  private nextSpawnZ = 0;
  private active: Segment[] = [];
  private currentSpeed = 10;
  private currentShape: Shape = "octagon";
  private maxActiveSegments = 6;
  private distance = 0;
  private complete = false;
  private endless = false;
  private elapsed = 0;
  // Most recent boss marker we emitted onBossStart for. Used to fire
  // onBossCleared exactly once when its corresponding bossEnd segment
  // recycles. null when no boss phrase is currently in flight.
  private activeBoss: BossMarker | null = null;

  constructor(factory: SegmentFactory, worldGroup: THREE.Object3D) {
    this.factory = factory;
    this.worldGroup = worldGroup;
  }

  setLevel(level: LevelEntry[]): void {
    this.reset();
    this.level = level;
    const first = level.find((e): e is ShapeSegment => !isGate(e));
    if (first) {
      this.currentShape = first.shape;
      this.currentSpeed = first.speed;
    }
    this.onShapeChanged.emit(this.currentShape);
    this.onSpeedChanged.emit(this.currentSpeed);
    this.fillAhead();
  }

  // Endless mode: skip the "keep last segment as victory platform" guard
  // and never fire onLevelComplete. The owner is expected to top-up
  // entries via appendEntries() before the cursor reaches the end.
  setEndless(endless: boolean): void {
    this.endless = endless;
  }

  // Push more entries onto the tail of the current level. Used by the
  // game layer to keep an EndlessGenerator's output flowing into the
  // streamer without ever exhausting the cursor.
  appendEntries(entries: LevelEntry[]): void {
    if (entries.length === 0) return;
    for (const e of entries) this.level.push(e);
    this.fillAhead();
  }

  // How many entries remain ahead of the spawn cursor. The game layer
  // checks this each frame and tops up via appendEntries() when low.
  cursorRemaining(): number {
    return Math.max(0, this.level.length - this.cursor);
  }

  reset(): void {
    for (const seg of this.active) {
      this.factory.detach(seg);
    }
    this.active.length = 0;
    this.cursor = 0;
    // Spawn the first segment so its leading edge sits a bit past the
    // player. Otherwise the player has no floor on frame 1 and instantly
    // falls into the void.
    this.nextSpawnZ = RECYCLE_PAST_PLAYER;
    this.worldGroup.position.z = 0;
    this.distance = 0;
    this.complete = false;
    this.endless = false;
    this.elapsed = 0;
    this.activeBoss = null;
  }

  isComplete(): boolean {
    return this.complete;
  }

  getCurrentShape(): Shape {
    return this.currentShape;
  }

  getCurrentSpeed(): number {
    return this.currentSpeed;
  }

  getDistance(): number {
    return this.distance;
  }

  setMaxActiveSegments(n: number): void {
    this.maxActiveSegments = Math.max(2, n);
  }

  update(dt: number): void {
    if (this.complete) return;
    this.elapsed += dt;
    const advance = this.currentSpeed * dt;
    this.worldGroup.position.z += advance;
    this.distance += advance;

    this.detectGateCrossings();
    this.recycleBehindPlayer();
    this.fillAhead();
    this.detectCompletion();
    this.animatePickups(dt);
  }

  private animatePickups(dt: number): void {
    for (const seg of this.active) {
      if (seg.pickups.length === 0) continue;
      this.factory.animatePickups(seg, dt, this.elapsed);
    }
  }

  private detectGateCrossings(): void {
    const playerZ = 0;
    for (const seg of this.active) {
      if (!seg.isGate) continue;
      const worldZ = seg.zCenter + this.worldGroup.position.z;
      const tagged = seg as Segment & { __crossed?: boolean };
      if (!tagged.__crossed && worldZ >= playerZ) {
        tagged.__crossed = true;
        this.currentShape = seg.shape;
        this.onGateCrossed.emit({ toShape: seg.shape, zCenter: seg.zCenter });
        this.onShapeChanged.emit(seg.shape);
      }
    }
  }

  private recycleBehindPlayer(): void {
    const wz = this.worldGroup.position.z;
    const cursorExhausted = this.cursor >= this.level.length;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const seg = this.active[i]!;
      // The segment occupies world Z in [zCenter - length/2 + wz,
      //                                  zCenter + length/2 + wz].
      // Worldgroup advances in +Z, so the BACK edge (smallest Z) is the
      // last part of the segment to pass the player. We only want to
      // recycle once that back edge has moved RECYCLE_PAST_PLAYER units
      // past the player at z = 0; otherwise we drop the floor while the
      // ball is still sitting on it.
      const backEdgeWorldZ = seg.zCenter - seg.length / 2 + wz;
      // In story mode, keep the very last segment of the level alive as
      // the victory platform so the player has a floor to stand on while
      // the success screen plays. Endless mode never has a "last"
      // segment so the guard is skipped.
      const isLastOfLevel =
        !this.endless &&
        cursorExhausted &&
        this.active.length === 1 &&
        !seg.isGate;
      if (backEdgeWorldZ > RECYCLE_PAST_PLAYER && !isLastOfLevel) {
        if (!seg.isGate) this.onSegmentCleared.emit(seg);
        // Pair bossEnd with onBossCleared. We compare against
        // activeBoss so a stray bossEnd flag (from a hand-authored
        // level that didn't open with a boss marker) doesn't fire
        // a phantom cleared event.
        if (seg.bossEnd && this.activeBoss !== null) {
          const marker = this.activeBoss;
          this.activeBoss = null;
          this.onBossCleared.emit({ marker });
        }
        this.factory.detach(seg);
        this.active.splice(i, 1);
      }
    }
  }

  private detectCompletion(): void {
    if (this.complete) return;
    // Endless mode is never "complete" — it only ends on player death.
    if (this.endless) return;
    if (this.cursor < this.level.length) return;
    if (this.active.length !== 1) return;
    const last = this.active[0]!;
    if (last.isGate) return;
    const centerWorldZ = last.zCenter + this.worldGroup.position.z;
    // Fire as soon as the player has rolled across the centre of the
    // final platform. They are still firmly on it, so the success
    // screen freezes the world with a floor under the ball.
    if (centerWorldZ > 0) {
      this.complete = true;
      this.onLevelComplete.emit();
    }
  }

  private fillAhead(): void {
    const wz = this.worldGroup.position.z;
    while (
      this.active.length < this.maxActiveSegments &&
      this.cursor < this.level.length
    ) {
      const entry = this.level[this.cursor]!;
      let seg: Segment;
      if (isGate(entry)) {
        seg = this.factory.buildGate(entry.to, this.nextSpawnZ - 0.75);
        this.nextSpawnZ -= 1.5;
      } else {
        const half = entry.length / 2;
        const center = this.nextSpawnZ - half;
        seg = this.factory.build(entry, center);
        this.nextSpawnZ -= entry.length;
        if (entry.speed !== this.currentSpeed) {
          this.currentSpeed = entry.speed;
          this.onSpeedChanged.emit(entry.speed);
        }
      }
      this.factory.attach(seg, this.worldGroup);
      this.active.push(seg);
      this.cursor += 1;
      // Fire the boss-start signal as soon as the segment marked with
      // `boss` enters the active set. We track it as the activeBoss so
      // we can pair the corresponding bossEnd recycle to onBossCleared.
      if (seg.boss) {
        this.activeBoss = seg.boss;
        this.onBossStart.emit({ marker: seg.boss });
      }

      const leadingWorldZ = seg.zCenter - seg.length / 2 + wz;
      if (leadingWorldZ < -SPAWN_AHEAD) break;
    }
  }

  syncBodies(): void {
    for (const seg of this.active) {
      this.factory.syncBodies(seg);
    }
  }

  getActive(): Segment[] {
    return this.active;
  }

  // Returns the SHAPE segment currently containing the player at world
  // z = 0 (gates are skipped because they have no panels to highlight).
  // Used by TileHighlight to find the floor tile underneath the ball.
  // Cheap O(active) -- there are typically <= 6 active segments.
  getSegmentAtPlayer(): Segment | null {
    const wz = this.worldGroup.position.z;
    for (const seg of this.active) {
      if (seg.isGate) continue;
      const centerWZ = seg.zCenter + wz;
      const half = seg.length / 2;
      if (centerWZ - half <= 0 && 0 <= centerWZ + half) {
        return seg;
      }
    }
    return null;
  }

  dispose(): void {
    this.reset();
    this.onGateCrossed.clear();
    this.onShapeChanged.clear();
    this.onSpeedChanged.clear();
    this.onLevelComplete.clear();
    this.onSegmentCleared.clear();
    this.onBossStart.clear();
    this.onBossCleared.clear();
  }
}
